import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { logVocalExerciseStart } from '../utils/activityLogger';
import { OpenSheetMusicDisplay } from 'opensheetmusicdisplay';
import CollapsibleSection from './CollapsibleSection';
import { loadSoundfontInstrument } from '../utils/soundfontInstruments';
import * as Tone from 'tone';
import {
  SCALE_TYPES,
  KEYS,
  buildScale,
  generateScaleMusicXML,
  midiToPlayableNote,
  getTonicName,
  prettyNoteName
} from '../utils/vocalScales';
import '../styles/ear-training.css';

const ScaleExercise = () => {
  const { t } = useLanguage();
  const { user } = useAuth();

  const [config, setConfig] = useState({
    scaleType: 'major',
    keyIndex: 0,
    startOctave: 3,
    range: '1', // '1', '1.5', '2' octaves
    direction: 'updown', // 'up', 'down', 'updown'
    tempo: 100,
    instrument: 'piano',
    volume: 0,
    loop: false
  });
  const [isPlaying, setIsPlaying] = useState(false);

  const osmdContainerRef = useRef(null);
  const externalOsmdContainer = useRef(null);
  const osmdRef = useRef(null);
  const playbackTimeouts = useRef([]);
  const instrumentRef = useRef(null);
  const loadedInstrumentKey = useRef(null);
  // Mirror of config.loop so an in-flight playback sees toggles immediately
  const loopRef = useRef(false);

  useEffect(() => {
    loopRef.current = config.loop;
  }, [config.loop]);

  // Same instrument list as the ear training activities
  const instruments = {
    random: { name: `🎲 ${t('earTraining.instruments.random')}`, soundfont: null },
    piano: { name: `🎹 ${t('earTraining.instruments.piano')}`, soundfont: 'piano' },
    electric_piano: { name: `🎹 ${t('earTraining.instruments.electricPiano')}`, soundfont: 'electric_piano' },
    guitar: { name: `🎸 ${t('earTraining.instruments.guitar')}`, soundfont: 'guitar' },
    bass: { name: `🎸 ${t('earTraining.instruments.bass')}`, soundfont: 'bass' },
    strings: { name: `🎻 ${t('earTraining.instruments.strings')}`, soundfont: 'strings' },
    violin: { name: `🎻 ${t('earTraining.instruments.violin')}`, soundfont: 'violin' },
    cello: { name: `🎻 ${t('earTraining.instruments.cello')}`, soundfont: 'cello' },
    brass: { name: `🎺 ${t('earTraining.instruments.brass')}`, soundfont: 'brass' },
    french_horn: { name: `🎺 ${t('earTraining.instruments.frenchHorn')}`, soundfont: 'french_horn' },
    trombone: { name: `🎺 ${t('earTraining.instruments.trombone')}`, soundfont: 'trombone' },
    saxophone: { name: `🎷 ${t('earTraining.instruments.saxophone')}`, soundfont: 'saxophone' },
    flute: { name: `🎵 ${t('earTraining.instruments.flute')}`, soundfont: 'flute' },
    clarinet: { name: `🎵 ${t('earTraining.instruments.clarinet')}`, soundfont: 'clarinet' },
    oboe: { name: `🎵 ${t('earTraining.instruments.oboe')}`, soundfont: 'oboe' },
    organ: { name: `🎼 ${t('earTraining.instruments.organ')}`, soundfont: 'organ' },
    choir: { name: `👥 ${t('earTraining.instruments.choir')}`, soundfont: 'choir' },
    bells: { name: `🔔 ${t('earTraining.instruments.bells')}`, soundfont: 'bells' },
    harp: { name: `🪕 ${t('earTraining.instruments.harp')}`, soundfont: 'harp' }
  };

  const getScaleData = useCallback(() => {
    return buildScale({
      scaleType: config.scaleType,
      keyEntry: KEYS[config.keyIndex],
      startOctave: config.startOctave,
      range: config.range,
      direction: config.direction
    });
  }, [config.scaleType, config.keyIndex, config.startOctave, config.range, config.direction]);

  // The stylesheet forces `fill: black !important` on every notation SVG element,
  // so the highlight must be set with inline-important priority on each leaf
  // element of the note group (a parent <g> alone loses to the direct rule).
  const clearHighlights = useCallback(() => {
    const svg = externalOsmdContainer.current?.querySelector('svg');
    if (!svg) return;
    svg.querySelectorAll('g.vf-note *').forEach((el) => {
      el.style.removeProperty('fill');
      el.style.removeProperty('stroke');
    });
  }, []);

  const highlightNote = useCallback((noteIndex, duration) => {
    const svg = externalOsmdContainer.current?.querySelector('svg');
    if (!svg) return;
    const noteGroups = svg.querySelectorAll('g.vf-note');
    const group = noteGroups[noteIndex];
    if (!group) return;
    const elements = group.querySelectorAll('*');
    elements.forEach((el) => {
      el.style.setProperty('fill', '#ff4444', 'important');
      el.style.setProperty('stroke', '#ff4444', 'important');
    });
    setTimeout(() => {
      elements.forEach((el) => {
        el.style.removeProperty('fill');
        el.style.removeProperty('stroke');
      });
    }, duration);
  }, []);

  const stopPlayback = useCallback(() => {
    playbackTimeouts.current.forEach(clearTimeout);
    playbackTimeouts.current = [];
    if (instrumentRef.current?.releaseAll) {
      instrumentRef.current.releaseAll();
    }
    clearHighlights();
    setIsPlaying(false);
  }, [clearHighlights]);

  // Create the OSMD container outside React's DOM management (avoids reconciler conflicts)
  useEffect(() => {
    if (osmdContainerRef.current && !externalOsmdContainer.current) {
      externalOsmdContainer.current = document.createElement('div');
      externalOsmdContainer.current.style.width = '100%';
      osmdContainerRef.current.appendChild(externalOsmdContainer.current);
    }
    return () => {
      if (externalOsmdContainer.current && osmdContainerRef.current) {
        try {
          osmdContainerRef.current.removeChild(externalOsmdContainer.current);
        } catch (e) {
          // Ignore errors during cleanup
        }
        externalOsmdContainer.current = null;
      }
    };
  }, []);

  // Render notation whenever the scale changes
  useEffect(() => {
    stopPlayback();
    const timer = setTimeout(async () => {
      if (!externalOsmdContainer.current) return;
      try {
        if (osmdRef.current) {
          try {
            osmdRef.current.clear();
          } catch (e) {
            // Ignore stale-instance errors
          }
          osmdRef.current = null;
        }
        osmdRef.current = new OpenSheetMusicDisplay(externalOsmdContainer.current, {
          autoResize: true,
          backend: 'svg',
          drawTitle: false,
          drawPartNames: false,
          drawingParameters: 'compacttight'
        });
        const { notes, fifths, clef } = getScaleData();
        const mode = SCALE_TYPES[config.scaleType].mode;
        await osmdRef.current.load(generateScaleMusicXML(notes, fifths, mode, clef));

        // Start at full size and shrink only as needed to fit the target
        // number of systems, down to a per-device readability floor
        const isMobile = window.innerWidth <= 768;
        let zoom = isMobile ? 0.5 : 1.0;
        const minZoom = isMobile ? 0.4 : 0.6;
        const maxSystems = 2;
        osmdRef.current.zoom = zoom;
        osmdRef.current.render();
        const countSystems = () => externalOsmdContainer.current?.querySelectorAll('g.staffline').length || 0;
        while (countSystems() > maxSystems && zoom > minZoom + 0.001) {
          zoom = Math.max(minZoom, zoom - 0.1);
          osmdRef.current.zoom = zoom;
          osmdRef.current.render();
        }
      } catch (error) {
        console.error('Error rendering scale notation:', error);
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [getScaleData, config.scaleType, stopPlayback]);

  // Stop audio timers on unmount
  useEffect(() => {
    return () => {
      playbackTimeouts.current.forEach(clearTimeout);
    };
  }, []);

  const ensureInstrument = async () => {
    let key = config.instrument;
    if (key === 'random') {
      const realInstruments = Object.keys(instruments).filter((k) => k !== 'random');
      key = realInstruments[Math.floor(Math.random() * realInstruments.length)];
    } else if (instrumentRef.current && loadedInstrumentKey.current === key) {
      return instrumentRef.current;
    }
    const instrument = await loadSoundfontInstrument(instruments[key].soundfont, config.volume);
    instrumentRef.current = instrument;
    loadedInstrumentKey.current = key;
    return instrument;
  };

  const playScale = async () => {
    if (isPlaying) return;
    try {
      await Tone.start();
      const instrument = await ensureInstrument();
      const { notes } = getScaleData();
      const beat = 60 / config.tempo;
      setIsPlaying(true);

      const keyName = getTonicName(KEYS[config.keyIndex], config.scaleType);
      logVocalExerciseStart(user, 'scale', {
        name: `${keyName} ${config.scaleType}`,
        scaleType: config.scaleType,
        key: keyName,
        startOctave: config.startOctave,
        range: config.range,
        direction: config.direction,
        tempo: config.tempo,
        instrument: config.instrument,
        loop: config.loop
      });

      const playPass = () => {
        notes.forEach((note, i) => {
          instrument.triggerAttackRelease(midiToPlayableNote(note.midi), beat * 0.95, Tone.now() + i * beat);
          playbackTimeouts.current.push(setTimeout(() => highlightNote(i, beat * 1000), i * beat * 1000));
        });

        // One-beat breath gap before a looped repeat; otherwise finish
        playbackTimeouts.current.push(
          setTimeout(() => {
            playbackTimeouts.current = [];
            if (loopRef.current) {
              playPass();
            } else {
              setIsPlaying(false);
            }
          }, notes.length * beat * 1000 + (loopRef.current ? beat * 1000 : 200))
        );
      };
      playPass();
    } catch (error) {
      setIsPlaying(false);
    }
  };

  const scaleData = getScaleData();
  const tonicName = getTonicName(KEYS[config.keyIndex], config.scaleType);
  const lowNote = scaleData.notes.reduce((a, b) => (a.midi < b.midi ? a : b));
  const highNote = scaleData.notes.reduce((a, b) => (a.midi > b.midi ? a : b));
  const noteLabel = (n) => prettyNoteName(n.step + (n.alter === 1 ? '#' : n.alter === -1 ? 'b' : n.alter === 2 ? '##' : n.alter === -2 ? 'bb' : '')) + n.octave;

  const renderConfigArea = () => (
    <div className="interval-config">
      <div className="config-header">
        <h4>{t('vocalExercises.config.title')}</h4>
      </div>

      {/* Scale Type */}
      <div className="config-section">
        <label>{t('vocalExercises.config.scaleType')}:</label>
        <select
          value={config.scaleType}
          onChange={(e) => setConfig((prev) => ({ ...prev, scaleType: e.target.value }))}
        >
          {Object.keys(SCALE_TYPES).map((type) => (
            <option key={type} value={type}>
              {t(`vocalExercises.scales.${type}`)}
            </option>
          ))}
        </select>
      </div>

      {/* Key */}
      <div className="config-section">
        <label>{t('vocalExercises.config.key')}:</label>
        <select
          value={config.keyIndex}
          onChange={(e) => setConfig((prev) => ({ ...prev, keyIndex: parseInt(e.target.value) }))}
        >
          {KEYS.map((key, index) => (
            <option key={key.pc} value={index}>
              {prettyNoteName(getTonicName(key, config.scaleType))}
            </option>
          ))}
        </select>
      </div>

      {/* Direction */}
      <div className="config-section">
        <label>{t('vocalExercises.config.direction')}:</label>
        <div className="mode-buttons">
          {[
            { value: 'up', label: `↗️ ${t('vocalExercises.directions.up')}` },
            { value: 'down', label: `↘️ ${t('vocalExercises.directions.down')}` },
            { value: 'updown', label: `↕️ ${t('vocalExercises.directions.upDown')}` }
          ].map((direction) => (
            <button
              key={direction.value}
              className={`mode-btn ${config.direction === direction.value ? 'active' : ''}`}
              onClick={() => setConfig((prev) => ({ ...prev, direction: direction.value }))}
            >
              {direction.label}
            </button>
          ))}
        </div>
      </div>

      {/* Starting Octave */}
      <div className="config-section">
        <label>{t('vocalExercises.config.startingOctave')}:</label>
        <select
          value={config.startOctave}
          onChange={(e) => setConfig((prev) => ({ ...prev, startOctave: parseInt(e.target.value) }))}
        >
          {[2, 3, 4, 5].map((octave) => (
            <option key={octave} value={octave}>
              {octave}
            </option>
          ))}
        </select>
      </div>

      {/* Range */}
      <div className="config-section">
        <label>{t('vocalExercises.config.range')}:</label>
        <select
          value={config.range}
          onChange={(e) => setConfig((prev) => ({ ...prev, range: e.target.value }))}
        >
          <option value="1">{t('vocalExercises.ranges.oneOctave')}</option>
          <option value="1.5">{t('vocalExercises.ranges.octaveAndAHalf')}</option>
          <option value="2">{t('vocalExercises.ranges.twoOctaves')}</option>
        </select>
      </div>

      {/* Tempo */}
      <div className="config-section">
        <label>{t('vocalExercises.config.tempo')}:</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <input
            type="range"
            min="40"
            max="180"
            step="5"
            value={config.tempo}
            onChange={(e) => setConfig((prev) => ({ ...prev, tempo: Number(e.target.value) }))}
            style={{ flex: 1 }}
          />
          <span style={{ minWidth: '60px', fontSize: '12px' }}>{config.tempo} BPM</span>
        </div>
      </div>

      {/* Loop */}
      <div className="config-section">
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={config.loop}
            onChange={(e) => setConfig((prev) => ({ ...prev, loop: e.target.checked }))}
          />
          <span>🔁 {t('vocalExercises.config.loop')}</span>
        </label>
      </div>

      {/* Instrument */}
      <div className="config-section">
        <label>{t('vocalExercises.config.instrument')}:</label>
        <select
          value={config.instrument}
          onChange={(e) => setConfig((prev) => ({ ...prev, instrument: e.target.value }))}
        >
          {Object.entries(instruments).map(([key, instrument]) => (
            <option key={key} value={key}>
              {instrument.name}
            </option>
          ))}
        </select>
      </div>
    </div>
  );

  return (
    <div className="interval-training-container scale-exercise-container">
      <div className="desktop-layout">
        <CollapsibleSection
          title={t('vocalExercises.config.title')}
          defaultExpanded={false}
          className="config-section-mobile"
        >
          {renderConfigArea()}
        </CollapsibleSection>

        <div className="config-area-desktop">{renderConfigArea()}</div>

        <div className="play-area-container">
          <div className="play-area">
            <div className="notation-container" style={{ padding: '8px', margin: '8px 0' }}>
              <div className="interval-notation-wrapper">
                <div className="interval-title">
                  <h4>
                    {prettyNoteName(tonicName)} {t(`vocalExercises.scales.${config.scaleType}`)}
                    <span style={{ fontWeight: 'normal', fontSize: '13px', marginLeft: '8px', color: '#94a3b8' }}>
                      {noteLabel(lowNote)} – {noteLabel(highNote)}
                    </span>
                  </h4>
                </div>
                <div
                  ref={osmdContainerRef}
                  className="interval-notation"
                  style={{
                    backgroundColor: 'white',
                    color: 'black',
                    filter: 'none',
                    position: 'relative',
                    minHeight: '80px',
                    padding: '4px'
                  }}
                />
              </div>
            </div>

            <div className="play-controls">
              <button
                className="play-interval-btn"
                onClick={isPlaying ? stopPlayback : playScale}
              >
                {isPlaying ? `⏹️ ${t('vocalExercises.stop')}` : `🔊 ${t('vocalExercises.play')}`}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScaleExercise;
